import { ArrowUpRight, Award, Medal, Trophy, Users } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import {
  type IndividualHonor,
  armlResultsService,
} from "../services/ArmlResultsService";

// The individual lists are read from Firestore rather than compiled in, so the
// students on them are not carried in the bundle or in git. They are shown by
// score rather than by published rank: rank is a tiebreaker artifact, and in
// 2025 every listed student answered every problem correctly, so the ordering
// only separates students who scored identically.
const useIndividualHonors = (season: string) => {
  const [honors, setHonors] = useState<IndividualHonor[] | null>(null);

  useEffect(() => {
    let current = true;
    armlResultsService.getIndividualHonors(season).then((rows) => {
      if (current) {
        setHonors(rows);
      }
    });
    return () => {
      current = false;
    };
  }, [season]);

  return honors;
};

const HonorsTable = ({
  rows,
  testId,
}: {
  rows: IndividualHonor[];
  testId: string;
}) => (
  <table className="mt-6 w-full border-collapse text-left" data-testid={testId}>
    <thead>
      <tr className="border-b border-slate-900/15">
        <th className="py-2 pr-3 text-xs font-bold uppercase tracking-widest text-slate-600">
          Score
        </th>
        <th className="py-2 pr-3 text-xs font-bold uppercase tracking-widest text-slate-600">
          Student
        </th>
        <th className="hidden py-2 text-right text-xs font-bold uppercase tracking-widest text-slate-600 sm:table-cell">
          Team
        </th>
      </tr>
    </thead>
    <tbody>
      {rows.map((student) => (
        <tr
          className="border-b border-slate-900/10 last:border-0"
          key={`${student.score}-${student.name}`}
        >
          <td className="py-2.5 pl-2 pr-3 font-black tabular-nums text-slate-500">
            {student.score}
          </td>
          <td className="py-2.5 pr-3 font-semibold">
            {student.name}
            {student.note ? (
              <span className="ml-2 text-sm font-bold text-emerald-800">
                {student.note}
              </span>
            ) : null}
            <span className="mt-0.5 block text-sm text-slate-500 sm:hidden">
              {student.team}
            </span>
          </td>
          <td className="hidden py-2.5 pr-2 text-right text-sm text-slate-500 sm:table-cell">
            {student.team}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Division A top eight, from the official ARML results. 2026's published
// summary stops at 7th, so 8th comes from the full team spreadsheet it links.
// Site codes are normalised across both seasons: the 2026 doc writes PSU/UNR/STA
// where the 2025 doc writes PENN/RENO, and St. Anselm is shown as ANSE.
const divisionA2026 = [
  { place: 1, team: "Texas A1", site: "IOWA", total: 236 },
  { place: 2, team: "Ontario Math Circles A1", site: "PENN", total: 217 },
  { place: 3, team: "Washington A4", site: "RENO", total: 213 },
  { place: 4, team: "Lehigh Valley A1", site: "PENN", total: 209 },
  { place: 5, team: "New York City A1", site: "PENN", total: 203 },
  { place: 6, team: "Chicago A1", site: "IOWA", total: 201 },
  { place: 7, team: "SFBA/NorCal A1", site: "RENO", total: 200 },
  { place: 8, team: "Lexington A1", site: "ANSE", total: 196 },
];

const divisionA2025 = [
  { place: 1, team: "Lehigh Valley A1", site: "PENN", total: 234 },
  { place: 2, team: "SFBA/NorCal A1", site: "RENO", total: 230 },
  { place: 3, team: "Washington A1", site: "RENO", total: 228 },
  { place: 4, team: "Lexington A1", site: "ANSE", total: 227 },
  { place: 5, team: "NYC A1", site: "PENN", total: 221 },
  { place: 6, team: "Texas A1", site: "IOWA", total: 220 },
  { place: 7, team: "TJHSST A1", site: "PENN", total: 219 },
  { place: 8, team: "Ontario Math Circles A1", site: "PENN", total: 209 },
];

const StandingsTable = ({
  rows,
  testId,
}: {
  rows: typeof divisionA2026;
  testId: string;
}) => (
  <table className="mt-6 w-full border-collapse text-left" data-testid={testId}>
    {/* The caption is the one place the division is named — the visible copy
        just says ARML, since Division A is the top division either way. */}
    <caption className="sr-only">
      Division A top eight teams, with Washington highlighted
    </caption>
    <thead>
      <tr className="border-b border-slate-900/15">
        <th className="py-2 pr-3 text-xs font-bold uppercase tracking-widest text-slate-600">
          Place
        </th>
        <th className="py-2 pr-3 text-xs font-bold uppercase tracking-widest text-slate-600">
          Team
        </th>
        <th className="hidden py-2 pr-3 text-xs font-bold uppercase tracking-widest text-slate-600 sm:table-cell">
          Site
        </th>
        <th className="py-2 text-right text-xs font-bold uppercase tracking-widest text-slate-600">
          Total
        </th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => {
        const isWashington = row.team.startsWith("Washington");
        return (
          <tr
            className={
              isWashington
                ? "bg-[#4b2e83] text-white"
                : "border-b border-slate-900/10 last:border-0"
            }
            key={row.place}
          >
            <td
              className={`py-2.5 pl-2 pr-3 font-black tabular-nums ${isWashington ? "" : "text-slate-500"}`}
            >
              {row.place}
              {isWashington ? (
                <span className="sr-only"> (Washington)</span>
              ) : null}
            </td>
            <td
              className={`py-2.5 pr-3 ${isWashington ? "font-black" : "font-semibold"}`}
            >
              {row.team}
            </td>
            <td
              className={`hidden py-2.5 pr-3 text-sm tabular-nums sm:table-cell ${isWashington ? "text-violet-200" : "text-slate-500"}`}
            >
              {row.site}
            </td>
            <td className="py-2.5 pr-2 text-right font-black tabular-nums">
              {row.total}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const ResultSource = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    className="inline-flex items-center gap-2 font-extrabold text-[#4b2e83] underline decoration-violet-300 underline-offset-4 hover:text-violet-700"
    href={href}
    target="_blank"
    rel="noreferrer"
  >
    {children}
    <ArrowUpRight size={16} aria-hidden="true" />
  </a>
);

const ArmlResults: React.FC = () => {
  const honors2026 = useIndividualHonors("2026");
  const honors2025 = useIndividualHonors("2025");

  return (
    <>
      <section className="bg-[#2d174d] py-20 text-white">
        <div className="site-shell">
          <p className="eyebrow text-emerald-300">
            Washington on the national stage
          </p>
          <h1 className="mt-5 max-w-5xl text-5xl font-black tracking-tight sm:text-7xl">
            Washington placed third at ARML in 2025 and 2026.
          </h1>
          <p className="mt-6 max-w-3xl text-xl leading-8 text-violet-100">
            The official results recognize ten Washington individual
            performances across those two seasons, led by Ian Rui in 6th place
            in 2026.
          </p>
        </div>
      </section>

      <section className="site-shell py-20">
        <div className="rounded-[2rem] bg-[#4b2e83] px-8 py-8 text-center text-white sm:px-12">
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl">
            3rd place, back to back.
          </h2>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <article className="result-card border-emerald-200 bg-emerald-100">
            <div className="flex items-center justify-between">
              <span className="eyebrow text-emerald-900">ARML 2026</span>
              <Medal
                className="text-emerald-800"
                size={32}
                aria-hidden="true"
              />
            </div>
            <StandingsTable rows={divisionA2026} testId="standings-2026" />
            <p className="mt-6 max-w-xl text-base leading-7 text-slate-700">
              WARML’s first time as the top scorer at the West Coast site.
            </p>
            <p className="mt-8">
              <ResultSource href="https://docs.google.com/document/d/1WN4rQoe5SjkQvxkZyBYMDoepZ-93OkFQLdI7pB2z3kE/edit">
                Official 2026 ARML results
              </ResultSource>
            </p>
          </article>

          <article className="result-card border-blue-200 bg-blue-100">
            <div className="flex items-center justify-between">
              <span className="eyebrow text-blue-900">ARML 2025</span>
              <Award className="text-blue-800" size={32} aria-hidden="true" />
            </div>
            <StandingsTable rows={divisionA2025} testId="standings-2025" />
            <p className="mt-6 max-w-xl text-base leading-7 text-slate-700">
              WARML’s first time in the nation’s top three.
            </p>
            <p className="mt-8">
              <ResultSource href="https://docs.google.com/document/d/17KQYnstIYxyz2r1KoyYIbkl-2Ex62jd1x-Cy-z8BcG8/edit">
                Official 2025 ARML results
              </ResultSource>
            </p>
          </article>
        </div>
      </section>

      <section className="site-shell pb-20">
        <p className="eyebrow text-[#4b2e83]">Individual honors</p>
        <h2 className="mt-4 max-w-4xl text-4xl font-black tracking-tight">
          Ten Washington students reached the official individual list across
          the two seasons.
        </h2>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <article className="result-card border-emerald-200 bg-emerald-100">
            <span className="eyebrow text-emerald-900">ARML 2026</span>
            <p className="mt-4 text-base leading-7 text-slate-700">
              Ian Rui and Alexander Yang each answered nine of ten, tied for
              second in the country and two of only four students at the Reno
              site to solve problems 1 through 9. Nobody at Reno solved the
              tenth, which fell only to the eventual national winner at Iowa.
              Five more Washington students answered eight and reached the
              tiebreaker round.
            </p>
            {honors2026 ? (
              <HonorsTable rows={honors2026} testId="honors-2026" />
            ) : null}
          </article>

          <article className="result-card border-blue-200 bg-blue-100">
            <span className="eyebrow text-blue-900">ARML 2025</span>
            <p className="mt-4 text-base leading-7 text-slate-700">
              Aryan Agrawal, Ian Rui, and Rick Zhou each answered every problem
              correctly. Aryan finished 11th in the national tiebreaker.
            </p>
            {honors2025 ? (
              <HonorsTable rows={honors2025} testId="honors-2025" />
            ) : null}
          </article>
        </div>
      </section>

      <section className="site-shell pb-20">
        <div className="rounded-[2rem] bg-emerald-100 p-8 sm:p-12 lg:grid lg:grid-cols-[auto_1fr] lg:items-center lg:gap-8">
          <div className="story-icon bg-emerald-300">
            <Users aria-hidden="true" />
          </div>
          <div className="mt-6 lg:mt-0">
            <p className="eyebrow text-emerald-900">ARML Local 2026</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight">
              WARML I-5 finishes 2nd at ARML Local
            </h2>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-700">
              Our stacked team — Daniel Ge, Edward Li, Ian Rui, Ryan Chen, Erin
              Bian, and Kevin Yang — finished in the top two spots behind only
              the team from Ontario, Canada. Many of our students scored 9/10
              individually to finish in a nationwide tie for third place, and
              Patrick Sun, representing{" "}
              <a
                className="font-extrabold text-emerald-900 underline decoration-emerald-400 underline-offset-4 hover:text-emerald-700"
                href="https://wamomath.org"
                target="_blank"
                rel="noreferrer"
              >
                WAMO
              </a>
              , had the fastest tiebreaker solve for Washington at 61 seconds.
              Several members of WAMO contributed to the team success of 2025
              WARML A1 and 2026 WARML A4.
            </p>
          </div>
        </div>
      </section>

      <section className="site-shell pb-20">
        <div className="rounded-[2rem] bg-violet-100 p-8 sm:p-12 lg:grid lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-8">
          <div className="story-icon bg-violet-300">
            <Trophy aria-hidden="true" />
          </div>
          <div className="mt-6 lg:mt-0">
            <p className="eyebrow text-violet-900">February 2026</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight">
              Washington Gold wins 2026 Harvard MIT Math Tournament
            </h2>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-700">
              Amy Cui and WARML’s Daniel Ge and Benjamin Fu, representing{" "}
              <a
                className="font-extrabold text-violet-900 underline decoration-violet-400 underline-offset-4 hover:text-violet-700"
                href="https://seattleinfinity.netlify.app/"
                target="_blank"
                rel="noreferrer"
              >
                Seattle Infinity Math Circle
              </a>
              , put together this squad of champions from local talent,
              including many members of WARML A1 and A4 and other local stars.
              The team had the highest score in Guts, fifth place in the Team
              Round, and brought home first place in the Sweepstakes, the
              overall team championship.
            </p>
          </div>
          <p className="mt-7 lg:mt-0">
            <ResultSource href="https://hmmt-archive.s3.amazonaws.com/tournaments/2026/feb/results/short.htm">
              Official HMMT results
            </ResultSource>
          </p>
        </div>
      </section>
    </>
  );
};

export default ArmlResults;
